<script setup lang="ts">
    import { GuidettiWorld } from '@/guidetti/GuidettiWorld';
    import useProductStore from '@/store/product.store';
    import Events from '@/services/Events';
    // import TweakPanel from '@/commons/TweakPanel';
    import { onMounted, onUnmounted, useTemplateRef, watch, computed, type PropType } from 'vue';
    import { storeToRefs } from 'pinia';
    import { useElementSize } from '@vueuse/core';
    import { MeshStandardMaterial } from 'three';
    import type { IImageField } from '@/typescript/interfaces/IImageField';
    import type { ITextField } from '@/typescript/interfaces/ITextField';
    import type { IProgressElement } from '@/commons/assetsManager/IProgressElement';

    let guidettiWorld: GuidettiWorld;
    const canvasContainerRef = useTemplateRef('canvas-container');
    const { width, height } = useElementSize(canvasContainerRef);
    // Get already existed elements
    const { selectedProductConfiguration, selectedFieldId } = storeToRefs(useProductStore());
    const filteredFields = computed(() =>
        selectedProductConfiguration.value.fields.filter(
            (filteredField) =>
                filteredField.transform.position.x + filteredField.transform.position.y !== 0 ||
                (filteredField as IImageField)?.url !== undefined ||
                (filteredField as ITextField)?.text !== undefined,
        ),
    );
    const props = defineProps({
        updateCustomMaterial: {
            type: Function as PropType<(fields: ITextField[] | IImageField[], getImage: boolean) => Promise<void>>,
            required: true,
        },
        customMaterial: {
            type: Object as PropType<MeshStandardMaterial>,
            required: true,
        },
        showPreview: {
            type: Boolean,
            default: false,
        },
    });
    // #region Vue focntionnal
    onMounted(() => {
        const progress: IProgressElement = {
            name: 'render-loop',
            progress: 0,
        };
        Events.emit('loading-start', progress.name);
        const loopAdvance = setTimeout(() => {
            if (progress.progress < 100) {
                progress.progress += 1;
                Events.emit('loading-progress', progress);
            } else {
                clearTimeout(loopAdvance);
            }
        }, 1000 / 120);

        // console.log('GuidettiWorld mounted', GuidettiWorld.instance);
        guidettiWorld = new GuidettiWorld({
            container: document.querySelector('#container-webgl-canvas') as HTMLDivElement,
            webGLParameters: {
                canvas: document.querySelector('#webgl-canvas') as HTMLCanvasElement,
                antialias: true,
            },
            customMaterial: props.customMaterial,
        });
        guidettiWorld.isLoaded.then(() => {
            guidettiWorld.mainLoop.start();
            Events.emit('loading-end', progress.name);
            clearTimeout(loopAdvance);
        });
        props.updateCustomMaterial(filteredFields.value, false);

        // TweakPanel.init({
        //     title: 'Guidetti',
        //     expanded: true,
        //     container: document.querySelector('#tweakpane-container') as HTMLDivElement,
        // });
    });

    watch([width, height], () => {
        if (width.value + height.value > 0) {
            guidettiWorld.resize();
            guidettiWorld.mainLoop.start();
        } else {
            guidettiWorld.mainLoop.stop();
        }
    });

    watch(selectedFieldId, (newSelectedFieldId) => {
        if (newSelectedFieldId !== undefined) {
            guidettiWorld.mainLoop.stop();
        } else {
            guidettiWorld.mainLoop.start();
            props.updateCustomMaterial(filteredFields.value, false);
        }
    });
    onUnmounted(() => {
        guidettiWorld.dispose();
    });
    // #endregion
</script>

<template>
    <div
        id="container-webgl-canvas"
        ref="canvas-container"
    >
        <canvas id="webgl-canvas" />
    </div>
</template>

<style scoped>
    #container-webgl-canvas {
        position: relative;
        width: 100%;
        height: 100%;
    }

    #webgl-canvas {
        position: relative;
        width: 100%;
        height: 100%;
    }

    #fullscreen {
        position: absolute;
        top: 128px;
        right: 1em;
        cursor: pointer;
    }

    #main-tweakpane-container {
        position: absolute;
        z-index: 100;
        top: 0;
        right: 0;
    }
    #bufferCanvas {
        background-color: red;
        width: auto;
        height: 100vh;
        position: fixed;
        top: 0;
        right: 25%;
        z-index: 100;
        pointer-events: none;
        opacity: 0;
    }
</style>
